import {
  getProductData,
  getProductPerks,
  getStoreProducts,
  checkoutWithPaypal,
  checkoutWithStripe,
  subscribeWithStripe,
  getAppliedPromoCodes,
  applyPromoCode,
  getGiftOrderById,
  getSubscriptionOptions,
  subscribeWithPaypal,
} from "../../api/store"
import { loadStripe } from "@stripe/stripe-js/pure"
import { get, head } from "lodash"
import axios from "@/api/axiosAuthRequired.js"
import Vue from "vue"
import Gift from "./store/gift"
import { COACHING_STEP } from "@/constants/coaching"
import { PAYMENT_FAIL_REASON } from "@/constants/payment"
import { INTERVAL_TYPE_ADVERBS } from "@/store/payment/payment.constants"
import { formatPriceUSD, formatPriceToNumber } from "@/store/utils/util.utils"
import { MIXINS } from "@/utils/mixins"

const state = {
  // gift/redeem page
  loginModalShow: false,
  guestModalShow: false,
  paymentModalShow: false,
  selectedProduct: null,
  perks: [],
  initialPerks: [],
  refillMethod: "",
  exactRefillProduct: {}, // Same shape as PG Point products
  productType: "",
  products: [], // Contains only data for PG Point products
  productsCache: {}, // Map of fetched product data
  errorMessage: "",
  paypalSubmitting: false,
  paypalError: null,
  paypalRedirectUrl: null,
  paypalRedirectQueryString: "",
  stripeSubmitting: false,
  stripe: null,
  stripeError: "",
  paymentIntentResponse: null,
  filledByStripe: false,
  subscribedItem: "",
  promoCodeSubmitting: false,
  promoCodeError: null,
  appliedPromoCode: null,
  paymentType: "purchase_for_self",
  stripeLast4: "",
  giftOrder: null,
  subscriptionOptions: [],
  savedPaymentMethods: [],
  selectedSavedCard: null,
  selectedPaypal: false,
}
const getters = {
  loginModalShow: state => state.loginModalShow,
  guestModalShow: state => state.guestModalShow,
  paymentModalShow: state => state.paymentModalShow,
  selectedProduct: state => state.selectedProduct,
  perks: state => state.perks,
  initialPerks: state => state.initialPerks,
  productType: state => state.productType,
  products: state => state.products,
  getProductData: state => productId => state.productsCache[productId] || null,
  errorMessage: state => state.errorMessage,
  paypalSubmitting: state => state.paypalSubmitting,
  paypalError: state => state.paypalError,
  paypalRedirectUrl: state => state.paypalRedirectUrl,
  paypalRedirectQueryString: state => state.paypalRedirectQueryString,
  stripeSubmitting: state => state.stripeSubmitting,
  stripe: state => state.stripe,
  stripeError: state => state.stripeError,
  refillMethod: state => state.refillMethod,
  exactRefillProduct: state => state.exactRefillProduct,
  filledByStripe: state => state.filledByStripe,
  subscribedItem: state => state.subscribedItem,
  promoCodeSubmitting: state => state.promoCodeSubmitting,
  promoCodeError: state => state.promoCodeError,
  appliedPromoCode: state => state.appliedPromoCode,
  paymentType: state => state.paymentType,
  stripeLast4: state => state.stripeLast4,
  giftOrder: state => state.giftOrder,
  subscriptionOptions: state => state.subscriptionOptions,
  savedPaymentMethods: state => state.savedPaymentMethods,
  selectedSavedCard: state => state.selectedSavedCard,
  maxDiscount: state => {
    if (state.subscriptionOptions && state.subscriptionOptions.length) {
      const product =
        state.subscriptionOptions[state.subscriptionOptions.length - 1].product
      const trialProduct =
        state.subscriptionOptions[state.subscriptionOptions.length - 1]
          .trial_product_offer
      const productPerks = product && product.perks ? product.perks : []
      const trialPerks =
        trialProduct && trialProduct.perks ? trialProduct.perks : []
      let discount = 0
      for (let perk of [...productPerks, ...trialPerks]) {
        if (perk.identifier === "pg_points_discount") {
          discount = Math.max(perk.percentage, discount)
        }
      }
      return discount
    }
    return 0
  },
  subscriptionFreePoints: state => {
    if (state.subscriptionOptions && state.subscriptionOptions.length) {
      const product =
        state.subscriptionOptions[state.subscriptionOptions.length - 1].product
      const productPerks = product && product.perks ? product.perks : []
      const productInitPerks =
        product && product.initial_perks ? product.initial_perks : []
      let freePoints = 0
      for (let perk of [...productPerks, ...productInitPerks]) {
        if (perk.identifier === "pg_points") {
          freePoints += perk.amount
        }
      }
      return freePoints
    }
    return 0
  },
  freeSessions: state => {
    if (!state.subscriptionOptions.length) {
      return 4
    }
    const product = state.subscriptionOptions[0].product
    const perks = [...(product.initial_perks || []), ...(product.perks || [])]
    let points = 0
    for (let perk of perks) {
      if (perk.identifier === "pg_points") {
        points += perk.amount
      }
    }
    return Math.floor(points / 1000)
  },
  selectedPaypal: state => state.selectedPaypal,
  trialProductOffer: state => {
    try {
      return state.subscriptionOptions[0].trial_product_offer
    } catch (error) {
      return null
    }
  },
  trialInitialInterval: state => {
    try {
      return state.subscriptionOptions[0].trial_product_offer.billing_details
        .trial
    } catch (error) {
      return {}
    }
  },
  trialRecurringInterval: state => {
    try {
      return state.subscriptionOptions[0].trial_product_offer.billing_details
        .regular
    } catch (error) {
      return {}
    }
  },
  trialInitialPrice: state => {
    try {
      return state.subscriptionOptions[0].trial_product_offer.billing_details
        .trial.price.amount
    } catch (error) {
      return 0
    }
  },
  trialRecurringPrice: state => {
    try {
      return state.subscriptionOptions[0].trial_product_offer.billing_details
        .regular.price.amount
    } catch (error) {
      return 0
    }
  },
  trialType: (state, getters) => {
    try {
      return INTERVAL_TYPE_ADVERBS[getters.trialRecurringInterval.interval_type]
    } catch (error) {
      return ""
    }
  },
  subscriptionProduct: state => {
    try {
      return state.subscriptionOptions[0].product
    } catch (error) {
      return {}
    }
  },
  subscriptionInterval: state => {
    try {
      return state.subscriptionOptions[0].product.billing_details.regular
    } catch (error) {
      return {}
    }
  },
  subscriptionInitialPrice: state => {
    try {
      return state.subscriptionOptions[0].product.billing_details.regular.price
        .amount
    } catch (error) {
      return {}
    }
  },
  subscriptionType: (state, getters) => {
    try {
      return INTERVAL_TYPE_ADVERBS[getters.subscriptionInterval.interval_type]
    } catch (error) {
      return ""
    }
  },
}
const mutations = {
  // Gift/redeem page
  setLoginModalShow: (state, status) => (state.loginModalShow = status),
  setGuestModalShow: (state, status) => (state.guestModalShow = status),
  setPaymentModalShow: (state, status) => (state.paymentModalShow = status),
  setSelectedProduct: (state, product) => (state.selectedProduct = product),
  setPerks: (state, perks) => (state.perks = perks),
  setInitialPerks: (state, perks) => (state.initialPerks = perks),
  setProductType: (state, productType) => (state.productType = productType),
  setProducts: (state, products) => (state.products = products),
  setProductData: (state, productData) => {
    const key = productData.id
    Vue.set(state.productsCache, key, productData)
  },
  setErrorMessage: (state, errorMessage) => (state.errorMessage = errorMessage),
  setPaypalSubmitting: (state, paypalSubmitting) =>
    (state.paypalSubmitting = paypalSubmitting),
  setPaypalError: (state, paypalError) =>
    (state.paypalError = paypalError),
  setPaypalRedirectUrl: (state, redirectUrl) =>
    (state.paypalRedirectUrl = redirectUrl),
  setPaypalRedirectQueryString: (state, queryString) =>
    (state.paypalRedirectQueryString = queryString),
  setStripeSubmitting: (state, stripeSubmitting) =>
    (state.stripeSubmitting = stripeSubmitting),
  setStripe: (state, stripe) => (state.stripe = stripe),
  setStripeError: (state, stripeError) => (state.stripeError = stripeError),
  setRefillMethod: (state, methods) => (state.refillMethod = methods),
  setExactRefillProduct: (state, exactRefillProduct) => (state.exactRefillProduct = exactRefillProduct),
  setPaymentIntentResponse: (state, res) => (state.paymentIntentResponse = res),
  setFilledByStripe: (state, filledByStripe) =>
    (state.filledByStripe = filledByStripe),
  setSubscribedItem: (state, subscribedItem) =>
    (state.subscribedItem = subscribedItem),
  setPromoCodeSubmitting: (state, promoCodeSubmitting) =>
    (state.promoCodeSubmitting = promoCodeSubmitting),
  setPromoCodeError: (state, promoCodeError) =>
    (state.promoCodeError = promoCodeError),
  setAppliedPromoCode: (state, appliedPromoCode) =>
    (state.appliedPromoCode = appliedPromoCode),
  setPaymentType: (state, paymentType) => (state.paymentType = paymentType),
  setStripeLast4: (state, stripeLast4) => (state.stripeLast4 = stripeLast4),
  setGiftOrder: (state, giftOrder) => (state.giftOrder = giftOrder),
  setSubscriptionOptions: (state, options) =>
    (state.subscriptionOptions = options),
  setSavedPaymentMethods: (state, methods) =>
    (state.savedPaymentMethods = methods),
  setSelectedSavedCard: (state, card) => (state.selectedSavedCard = card),
  setSelectedPaypal: (state, selectedPaypal) =>
    (state.selectedPaypal = selectedPaypal),
}
const actions = {
  async getRecaptchaToken () {
    await Vue.prototype.$recaptchaLoaded()
    const token = await Vue.prototype.$recaptcha("submit")
    return token
  },
  async loadProductData ({ state, commit }, productId) {
    let productData = state.productsCache[productId] || null
    if (productId === "exact-refill-product") {
      productData = state.exactRefillProduct
    } else if (productData == null) {
      productData = await getProductData(productId)
    }
    commit("setProductData", productData)
    return productData
  },
  async getProductPerks ({ state, commit }, productId) {
    const data = await getProductPerks(productId)
    commit("setPerks", data.perks || [])
    commit("setInitialPerks", data.initial_perks || [])
    commit("setProductType", data.product_type)
  },
  async fetchProducts ({ commit }) {
    const data = await getStoreProducts()
    await commit("setProducts", data.items)
  },
  async payWithPaypal (
    { commit, state, rootGetters },
    { orderType, product, promoCodes }
  ) {
    if (product.id === "exact-refill-product") {
      orderType = "credit-refill"
    }

    try {
      commit("setErrorMessage", "")
      let giftProductAssetVariationId = ""
      let senderName = ""
      let recipientName = ""
      let recipientEmail = ""
      let message = ""
      promoCodes = promoCodes || [get(state.appliedPromoCode, "promo_code.code")].filter(x => x)
      if (orderType === "gift") {
        const gift = rootGetters["Store/Gift/currentGift"]
        const giftForm = rootGetters["Store/Gift/giftForm"]
        const currentProMembershipImage =
          rootGetters["Store/Gift/currentProMembershipImage"]
        if (currentProMembershipImage) {
          giftProductAssetVariationId = currentProMembershipImage.id
        } else {
          giftProductAssetVariationId = gift.asset_variations[0].id
        }
        senderName = giftForm.yourName
        recipientName = giftForm.recipientName
        recipientEmail = giftForm.recipientEmail
        if (giftForm.giftMessage) {
          message = giftForm.giftMessage
        }
      }
      if (state.selectedProduct) {
        MIXINS.methods.trackAnalyticsEvent("Buys Store Points", {
          points_package: state.selectedProduct.amount,
          cost: state.selectedProduct.price,
          payment_type: "paypal",
        })
      }

      const data = await checkoutWithPaypal(
        product,
        state.paypalRedirectUrl,
        state.paypalRedirectQueryString,
        promoCodes,
        giftProductAssetVariationId,
        senderName,
        recipientName,
        recipientEmail,
        message,
        orderType
      )

      if (data.status === "failure") {
        const errorMessage = data?.message || ""
        commit("setErrorMessage", errorMessage)
        return
      }

      const authUrl = data?.auth_url
      if (!authUrl) {
        commit("setErrorMessage", "We were unable to direct you to the payment portal. Please try again later or contact us for help.")
        return
      }

      window.location = authUrl
    } catch (error) {
      console.error(error)
      commit(
        "setErrorMessage",
        "An unknown error occurred. Please try it again later or contact us for help."
      )
    }
  },
  async payWithStripe ({ commit, state, dispatch }, { body, orderType }) {
    if (state.stripeSubmitting) {
      return
    }
    if (orderType) {
      commit("setPaymentType", orderType)
    }
    // FB Pixel: Track "AddPaymentInfo"
    dispatch("Tracking/fbQuery", { event: "AddPaymentInfo" }, { root: true })
    // Analytics
    MIXINS.methods.trackAnalyticsEvent("add_payment_info")

    commit("setStripeSubmitting", true)

    try {
      if (state.selectedSavedCard) {
        commit("setStripeLast4", state.selectedSavedCard.last4)
        await dispatch("handleStripeMethod", state.selectedSavedCard.id)
      } else {
        const token = await dispatch("getRecaptchaToken")
        body.append("g_recaptcha_response", token)
        const res = await axios({
          method: "POST",
          url: `${process.env.PROGUIDES_WEB_URL}/payment/stripe/createPaymentMethod.php`,
          data: body,
        })
        if (res.error) {
          commit("setStripeError", res.error.message)
          await commit("setStripeSubmitting", false)
        } else if (res.status === "failure") {
          commit("setStripeError", res.data.message)
          await commit("setStripeSubmitting", false)
        } else {
          const stripePaymentId = res.data.payment_method.id
          commit("setStripeLast4", res.data.payment_method.card.last4)
          await dispatch("handleStripeMethod", stripePaymentId)
        }
      }
    } catch (error) {
      console.error(error)
      commit("setStripeError", error.message)
      await commit("setStripeSubmitting", false)
    }
  },
  async loadStripe ({ commit }) {
    try {
      commit("setStripe", await loadStripe(process.env.STRIPE_PUBLIC_KEY))
      // if (!cancelGenerating) {
      //   dispatch("generateStripeElement")
      // }
    } catch (error) {
      console.error(error)
      commit("setStripeError", error.message)
      await commit("setStripeSubmitting", false)
    }
  },
  async handleStripeMethod (
    { state, commit, dispatch, rootGetters },
    stripePaymentId
  ) {
    const body = new FormData()
    const token = await dispatch("getRecaptchaToken")
    body.append("g_recaptcha_response", token)
    body.append("payment_method_id", stripePaymentId)

    const promoCode = get(state.appliedPromoCode, "promo_code.code")
    if (promoCode) {
      body.append("promo_code", promoCode)
    }

    if (state.paymentType === "gift") {
      body.append("order_type", "gift")
      const gift = rootGetters["Store/Gift/currentGift"]
      const giftForm = rootGetters["Store/Gift/giftForm"]
      const currentProMembershipImage =
        rootGetters["Store/Gift/currentProMembershipImage"]
      body.append("product_ids[]", gift.product.id)
      body.append("description", gift.product.description)
      if (currentProMembershipImage) {
        body.append(
          "gift_product_asset_variation_id",
          currentProMembershipImage.id
        )
      } else {
        body.append(
          "gift_product_asset_variation_id",
          gift.asset_variations[0].id
        )
      }
      body.append("sender_name", giftForm.yourName)
      body.append("recipient_name", giftForm.recipientName)
      body.append("recipient_email", giftForm.recipientEmail)
      if (giftForm.giftMessage) {
        body.append("message", giftForm.giftMessage)
      }
    } else if (state.selectedProduct) {
      const product = state.selectedProduct
      body.append("product_ids[]", product.id)
      body.append("description", product.amount + " PG Points")
    } else {
      body.append("product_ids[]", state.selectedProduct.id)
      body.append("description", state.selectedProduct.amount + " PG Points")
    }
    const response = await checkoutWithStripe(body)

    await commit("setPaymentIntentResponse", response)
    await dispatch("handleStripePaymentIntentResponse")
  },
  async handleStripeSubscription ({ state, commit, dispatch }, { stripePaymentId, promoCodes }) {
    let body = new FormData()
    const token = await dispatch("getRecaptchaToken")
    body.append("g_recaptcha_response", token)
    body.append("payment_method_id", stripePaymentId)
    body.append("session_id", window.localStorage.getItem("session_identifier"))
    body.append("game", "all")
    body.append("plan_type", "annual")
    if (state.selectedProduct) {
      body.append("product_id", state.selectedProduct.id)
    }
    promoCodes = promoCodes || []
    promoCodes.forEach(promoCode => {
      body.append("promo_codes[]", promoCode)
    })
    await commit("setPaymentIntentResponse", await subscribeWithStripe(body))
    await dispatch("handleStripeSubscriptionIntentResponse")
  },
  async handleStripeSubscriptionIntentResponse ({ state, commit }) {
    switch (state.paymentIntentResponse.status) {
      case "requires_action":
        const { error } = await state.stripe.confirmCardPayment(
          state.paymentIntentResponse.payment_intent_client_secret
        )
        if (error) {
          commit("setStripeError", error.message)
          await commit("setStripeSubmitting", false)
        } else {
          // Artificially delay success to allow webhook to process invoice.
          await new Promise(resolve => {
            setTimeout(() => {
              resolve()
            }, 1500)
          })
          await commit(
            "setSubscribedItem",
            state.paymentIntentResponse.purchase_item
          )
          commit("setFilledByStripe", true)
        }
        break
      case "requires_payment_source":
      case "failure":
        if (
          state.paymentIntentResponse.reason ===
          PAYMENT_FAIL_REASON["CARD_TRIAL_INELIGIBLE"]
        ) {
          commit("Payment/setTrialIneligibility", true, { root: true })
        }
        commit("setStripeError", state.paymentIntentResponse.message)
        await commit("setStripeSubmitting", false)
        break
      case "success":
        // if (state.paymentType === "gift") {
        //   await commit("setGiftOrder", state.paymentIntentResponse.order)
        // } else {
        await commit(
          "setSubscribedItem",
          state.paymentIntentResponse.purchase_item
        )
        // }
        commit("setFilledByStripe", true)
    }
  },
  async handleStripePaymentIntentResponse ({
    state,
    commit,
    dispatch,
    rootGetters,
  }) {
    switch (state.paymentIntentResponse.status) {
      case "requires_action":
        const result = await state.stripe.handleCardAction(
          state.paymentIntentResponse.payment_intent_client_secret
        )
        if (result.error) {
          commit("setStripeError", result.error.message)
          await commit("setStripeSubmitting", false)
          return
        }
        const body = new FormData()
        body.append("payment_intent_id", result.paymentIntent.id)
        const promoCode = get(state.appliedPromoCode, "promo_code.code")
        if (promoCode) {
          body.append("promo_code", promoCode)
        }
        if (state.paymentType === "gift") {
          body.append("order_type", "gift")
          const gift = rootGetters["Store/Gift/currentGift"]
          const giftForm = rootGetters["Store/Gift/giftForm"]
          const currentProMembershipImage = rootGetters(
            "Store/Gift/currentProMembershipImage"
          )
          body.append("product_ids[]", gift.product.id)
          body.append("description", gift.product.description)
          if (currentProMembershipImage) {
            body.append(
              "gift_product_asset_variation_id",
              currentProMembershipImage.id
            )
          } else {
            body.append(
              "gift_product_asset_variation_id",
              gift.asset_variations[0].id
            )
          }
          body.append("sender_name", giftForm.yourName)
          body.append("recipient_name", giftForm.recipientName)
          body.append("recipient_email", giftForm.recipientEmail)
          if (giftForm.giftMessage) {
            body.append("message", giftForm.giftMessage)
          }
        } else if (state.selectedProduct) {
          const product = state.selectedProduct
          body.append("product_ids[]", product.id)
          body.append("description", product.amount + " PG Points")
        } else {
          body.append("product_ids[]", state.selectedProduct.id)
          body.append(
            "description",
            state.selectedProduct.amount + " PG Points"
          )
        }
        await commit("setPaymentIntentResponse", await checkoutWithStripe(body))
        await dispatch("handleStripePaymentIntentResponse")
        break
      case "requires_payment_source":
      case "failure":
        commit("setStripeError", state.paymentIntentResponse.message)
        await commit("setStripeSubmitting", false)
        break
      case "success":
        if (state.paymentType === "gift") {
          await commit("setGiftOrder", state.paymentIntentResponse.order)
        } else if (state.selectedProduct) {
          MIXINS.methods.trackAnalyticsEvent("Buys Store Points", {
            points_package: state.selectedProduct.amount,
            cost: state.selectedProduct.price,
            payment_type: "CC",
          })
        }
        dispatch("finishPayment")
        commit("setFilledByStripe", true)
    }
  },
  async finishPayment ({ commit, dispatch }) {
    await commit("setRefillMethod", "")
    await commit("setSelectedProduct", null)
    await commit("setPaymentIntentResponse", null)
    // await commit("setStripeCreditCardElements", {
    //   cardNumber: "",
    //   cvv: "",
    //   expirationDate: "",
    // })
    await commit("setStripeError", "")
    await commit("setStripeSubmitting", false)
    await dispatch("User/Inventory/loadInventory", true, { root: true })
    await commit("Instapro/Vod/setShowRefill", false, { root: true })
    await commit("Coaching/CoachService/setStep", COACHING_STEP.SELECTING, {
      root: true,
    })
  },
  async createExactRefillProduct ({ commit }, requiredFundsInUsd) {
    let price = formatPriceToNumber(formatPriceUSD(requiredFundsInUsd))
    await commit("setExactRefillProduct", {
      amount: requiredFundsInUsd * 100,
      bonus: 0,
      icon: null,
      id: "exact-refill-product",
      list_price: price,
      price,
      savings: 0,
    })
  },
  async subscribeWithPaypal ({ state, dispatch, commit }, { promoCodes }) {
    if (state.paypalSubmitting) {
      return
    }

    commit("setPaypalSubmitting", true)
    let body = new FormData()
    body.append("game", "all")
    body.append("plan_type", "annual")
    if (state.selectedProduct) {
      body.append("product_id", state.selectedProduct.id)
    }
    body.append("session_id", window.localStorage.getItem("session_identifier"))
    promoCodes = promoCodes || []
    promoCodes.forEach(promoCode => {
      body.append("promo_codes[]", promoCode)
    })
    const data = await subscribeWithPaypal(
      body,
      state.paypalRedirectQueryString
    )
    if (data.status === "success") {
      dispatch("trackUserPaymentMethodSelectionEvent", "PayPal")
      window.location = data.auth_url
    } else {
      commit("setPaypalError", data?.message || "An unexpected error occurred.")
    }
    commit("setPaypalSubmitting", false)
  },
  trackUserPaymentMethodSelectionEvent ({ dispatch }, paymentMethod) {
    // FB Pixel: Track "InitiateCheckout"
    dispatch("Tracking/fbQuery", { event: "InitiateCheckout" }, { root: true })
    // Analytics
    MIXINS.methods.trackAnalyticsEvent("set_checkout_option", {
      checkout_option: paymentMethod,
    })
  },
  trackUserPaymentInfoEnteredEvent ({ dispatch }) {
    // FB Pixel: Track "AddPaymentInfo"
    dispatch("Tracking/fbQuery", { event: "AddPaymentInfo" }, { root: true })
    // Analytics
    MIXINS.methods.trackAnalyticsEvent("add_payment_info")
  },
  async subscribeWithStripe ({ commit, dispatch, state }, { paymentMethodBody, promoCodes }) {
    if (state.stripe && !state.stripeSubmitting) {
      commit("setStripeSubmitting", true)
      commit("setStripeError", "")
      dispatch("trackUserPaymentInfoEnteredEvent")
      try {
        if (state.selectedSavedCard) {
          await dispatch("handleStripeSubscription", {
            stripePaymentId: state.selectedSavedCard.id,
            promoCodes: promoCodes,
          })
        } else {
          const token = await dispatch("getRecaptchaToken")
          paymentMethodBody.append("g_recaptcha_response", token)
          const res = await axios({
            method: "POST",
            url: `${process.env.PROGUIDES_WEB_URL}/payment/stripe/createPaymentMethod.php`,
            data: paymentMethodBody,
          })
          if (res.data.status === "failure") {
            commit("setStripeError", res.data.message)
          } else {
            const stripePaymentId = res.data.payment_method.id
            await dispatch("handleStripeSubscription", { stripePaymentId, promoCodes })
          }
        }
      } catch (error) {
        console.error(error)
        commit("setStripeError", error.message)
      } finally {
        commit("setStripeSubmitting", false)
      }
    }
  },
  resetStripe ({ commit }) {
    commit("setStripeError", "")
    commit("setStripeSubmitting", false)
    commit("setPaymentIntentResponse", null)
    commit("setFilledByStripe", false)
    commit("setGiftOrder", null)
  },
  async loadAppliedPromoCode ({ commit }) {
    try {
      const appliedPromoCodes = await getAppliedPromoCodes()
      const appliedPromoCode = head(appliedPromoCodes)
      commit("setAppliedPromoCode", appliedPromoCode || null)
    } catch (e) {
      console.error("Failed to load applied promo codes.")
    }
  },
  async applyPromoCode ({ commit, state }, code) {
    if (!state.promoCodeSubmitting) {
      commit("setPromoCodeError", null)
      commit("setPromoCodeSubmitting", true)

      try {
        const appliedPromoCode = await applyPromoCode(code)
        commit("setAppliedPromoCode", appliedPromoCode)
      } catch (e) {
        const errorStatus = get(e, "request.status")
        if (errorStatus === 422) {
          let error
          try {
            const response = JSON.parse(get(e, "request.response"))
            error = response.error
          } catch (e) {
            error = "Failed to apply Promo Code."
          }
          commit("setPromoCodeError", error)
        }
      } finally {
        commit("setPromoCodeSubmitting", false)
      }
    }
  },
  async getGiftOrderById ({ commit }, orderId) {
    const data = await getGiftOrderById(orderId)
    commit("setGiftOrder", data)
  },
  async getSubscriptionOptions ({ commit, state }) {
    if (state.subscriptionOptions && state.subscriptionOptions.length) {
      return
    }
    commit("setSubscriptionOptions", await getSubscriptionOptions())
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    Gift,
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/store.js